import { Directive, OnDestroy, inject } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { NBarclaycardApplePay } from '@df/module-barclaycard/apple-pay/barclaycard-apple-pay.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { IBasketData } from '@df/session/api/basket/basket';
@Directive()
// tslint:disable-next-line: directive-class-suffix
export class BarclaycardApplePayComponentDef extends AbstractCheckoutPayment implements OnDestroy {
    methodCode = NBarclaycardApplePay.methodCode;

    /**
     * Apple pay validate status
     * @type {Status}
     */
    availableStatus = new Status();

    /**
     * Apple pay in progress status
     * @type {Status}
     */
    inProgressStatus = new Status();

    /**
     * Apple Pay validate merchant status
     * @type {Status}
     */
    validateMerchantStatus = new Status();

    /**
     * Apple Pay validate status
     * @type {Status}
     */
    getRequestDataStatus = new Status();

    /**
     * Apple Pay request data
     * @type {ApplePay.IPaymentRequest}
     */
    private requestData?: ApplePay.IPaymentRequest;

    /**
     * Last checkout grand total, used to check if it changed, so if we should update questest data or not
     * @type {number}
     */
    lastTotal?: number;

    /**
     * If Apple Pay is available as payment method in correct version.
     * @type {boolean}
     */
    available?: boolean;

    private applePaySessionInstance?: ApplePay.ISessionInstance;

    private applePayService = inject(ApplePayService);

    private timeoutService = inject(TimeoutService);

    private dfEventService = inject(DfEventService);

    /**
     * Returns if we want to preload scripts and initiate form on component init
     * @returns {boolean}
     */
    protected override preload(): boolean {
        return true;
    }

    /**
     * loads new apple pay request data from BE
     */
    private updateRequestData?: () => void;

    protected override initialize(): void {
        // Only initialize if Apple Pay is available
        if (this.applePayService.isAvailable && this.applePayService.isSupportedVersion(NBarclaycardApplePay.version)) {
            this.loadScripts();
        } else {
            this.availableStatus.error = NBarclaycardApplePay.notAvailableMessage;
            this.detectChanges();
        }
    }

    /**
     * No scripts to load, this is fine
     */
    protected getJsAssets(): string[] {
        return [];
    }

    protected override onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        this.updateRequestData = this.timeoutService.debounce(() => {
            this.requestData = this.lastTotal = this.available = undefined;
            this.detectChanges();
            if (this.getRequestDataStatus.promise?.cancel) {
                this.getRequestDataStatus.promise.cancel();
            }
            this.app.request.send(this.getRequestDataStatus, { path: NBarclaycardApplePay.EEndpoints.request }, result => {
                this.requestData = result.request;
                this.lastTotal = parseFloat(result.request.total.amount);
                this.available = true;
                this.detectChanges();
            });
        }, 300);
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if ((event.id == 'dfCheckoutChange', (<any>event.data).type == 'paymentMethod')) {
                    this.updateRequestData?.();
                }
            })
        );
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => {
                if (this.getTotal() !== this.lastTotal) {
                    this.updateRequestData?.();
                }
            })
        );
        this.updateRequestData();
        this.subscriptions.add(this.getRequestDataStatus.subscribe(() => this.detectChanges()));
    }

    /**
     * On pay with Apple Pay button click
     */
    submit(): void {
        if (this.isApplePaySubmitAllowed) {
            this.inProgressStatus.busy = true;
            this.detectChanges();
            const basketData = this.app.customer.basket.data;
            const checkoutData = this.checkoutFormComponent.getSubmissionData();
            if (window.ApplePaySession && this.requestData) {
                this.applePaySessionInstance = new window.ApplePaySession(NBarclaycardApplePay.version, this.requestData);
                this.applePaySessionInstance.onvalidatemerchant = (e: ApplePay.IValidateMerchantEvent) =>
                    this.onValidateMerchant(e, this.applePaySessionInstance);
                this.applePaySessionInstance.onpaymentauthorized = (e: ApplePay.IPaymentEvent) =>
                    this.onPaymentAuthorized(e, checkoutData, basketData, this.applePaySessionInstance);
                this.applePaySessionInstance.oncancel = () => this.clear();
                this.applePaySessionInstance.begin();
            } else {
                this.clear();
            }
        } else {
            this.checkoutFormComponent.validateCheckout();
        }
    }

    private get isApplePaySubmitAllowed(): boolean {
        return (
            this.checkoutFormComponent.isCheckoutValid &&
            !!this.requestData &&
            !this.checkoutSubmitStatus.busy &&
            !this.getRequestDataStatus.busy &&
            !this.inProgressStatus.busy &&
            !!window.ApplePaySession
        );
    }

    private onValidateMerchant(e: ApplePay.IValidateMerchantEvent, applePaySessionInstance?: ApplePay.ISessionInstance): void {
        if (!applePaySessionInstance) {
            this.clear();
            return;
        }
        this.app.request.send(
            this.checkoutSubmitStatus,
            {
                path: NBarclaycardApplePay.EEndpoints.validate,
                data: {
                    url: e.validationURL
                }
            },
            data => {
                if (data.session) {
                    try {
                        applePaySessionInstance.completeMerchantValidation(data.session);
                    } catch {
                        this.onError(applePaySessionInstance);
                    }
                } else {
                    this.onValidateError(applePaySessionInstance);
                }
            },
            () => this.onValidateError(applePaySessionInstance)
        );
    }

    private onValidateError(applePaySessionInstance?: ApplePay.ISessionInstance): void {
        if (!applePaySessionInstance) {
            this.clear();
            return;
        }
        applePaySessionInstance.abort();
        this.validateMerchantStatus.error = 'Apple Pay failed to load.';
        this.clear();
    }

    private onPaymentAuthorized(
        e: ApplePay.IPaymentEvent,
        checkoutData: Checkout.ISubmissionData,
        basketData: IBasketData,
        applePaySessionInstance?: ApplePay.ISessionInstance
    ): void {
        if (!applePaySessionInstance) {
            this.clear();
            return;
        }
        this.app.request.send(
            this.checkoutSubmitStatus,
            {
                path: NBarclaycardApplePay.EEndpoints.order,
                data: {
                    payment: e.payment,
                    guestEmail: checkoutData.email
                }
            },
            result => {
                // Order is complete, we can execute standard order submit success flow now.
                if (window.ApplePaySession) {
                    try {
                        applePaySessionInstance.completePayment({ status: window.ApplePaySession.STATUS_SUCCESS });
                    } catch {
                        this.onError(applePaySessionInstance);
                    }
                }

                const eventDdata: Checkout.ISubmitSuccessEventData = {
                    request: checkoutData,
                    basket: basketData,
                    response: result
                };

                this.app.events.broadcast<Checkout.ISubmitSuccessEventData>(Checkout.EVENTS.SubmitSuccess, eventDdata);

                if (!result.post_redirect && !result.external_redirect && !result.preventSuccess) {
                    // keep subbmission as busy, we are redirecting now
                    // (that will prevent empty basket redirect)
                    this.checkoutFormComponent.status.superBusy = true;
                    this.checkoutFormComponent.redirectToSuccess();
                } else if (result.external_redirect) {
                    this.checkoutFormComponent.status.superBusy = true;
                }
            },
            () => this.onError(applePaySessionInstance)
        );
    }

    private onError(applePaySessionInstance: ApplePay.ISessionInstance): void {
        window.ApplePaySession && applePaySessionInstance?.completePayment({ status: window.ApplePaySession.STATUS_FAILURE });
        this.updateRequestData?.();
        this.clear();
    }

    /**
     * Returns total amount to pay
     * @returns {number | undefined}
     */
    private getTotal(): number | undefined {
        return this.app.customer.basket.data?.totals?.grand_total;
    }

    private clear(): void {
        this.inProgressStatus.busy = false;
        this.detectChanges();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        // this.applePaySessionInstance?.abort();
        this.clear();
    }
}
